import React, { useState, useRef, useEffect, useMemo } from 'react';
import { HiCalendar, HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { isSameDay, getDaysInMonth, getFirstDayOfMonth, formatDateForDisplay, parseDateInput, isDateValid } from '../../../utils/dateFormatters';
import { useGeneral } from '../../../contexts';
import styles from './DatePicker.module.css';

export interface DatePickerProps {
  value: string; // YYYY-MM-DD format
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: boolean;
  minDate?: string; // YYYY-MM-DD format
  maxDate?: string; // YYYY-MM-DD format
  locale?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  'data-field'?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder,
  label,
  error = false,
  minDate,
  maxDate,
  locale,
  className = '',
  disabled = false,
  id,
  'data-field': dataField,
}) => {
  const { language } = useGeneral();
  const currentLocale = locale || language.language;
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    if (value) {
      const date = parseDateInput(value);
      return date || new Date();
    }
    return new Date();
  });
  
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Parse value to Date
  const selectedDate = useMemo(() => {
    return value ? parseDateInput(value) : null;
  }, [value]);

  // Parse min/max dates
  const minDateObj = useMemo(() => minDate ? parseDateInput(minDate) : undefined, [minDate]);
  const maxDateObj = useMemo(() => maxDate ? parseDateInput(maxDate) : undefined, [maxDate]);

  // Update currentMonth when value changes
  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    }
  }, [selectedDate]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Get current month/year
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  // Calculate days in month and first day of month
  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOfMonth = getFirstDayOfMonth(year, month);

  // Get today's date
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Navigate to previous month
  const handlePrevMonth = () => {
    setCurrentMonth(new Date(year, month - 1, 1));
  };

  // Navigate to next month
  const handleNextMonth = () => {
    setCurrentMonth(new Date(year, month + 1, 1));
  };

  // Handle day click
  const handleDayClick = (day: number) => {
    const clickedDate = new Date(year, month, day);
    clickedDate.setHours(0, 0, 0, 0);

    // Check if date is valid
    if (!isDateValid(clickedDate, minDateObj || undefined, maxDateObj || undefined)) {
      return;
    }

    // Format as YYYY-MM-DD
    const yearStr = clickedDate.getFullYear();
    const monthStr = String(clickedDate.getMonth() + 1).padStart(2, '0');
    const dayStr = String(clickedDate.getDate()).padStart(2, '0');
    const dateString = `${yearStr}-${monthStr}-${dayStr}`;

    onChange(dateString);
    setIsOpen(false);
  };

  // Get month name
  const monthName = new Intl.DateTimeFormat(
    currentLocale === 'en' ? 'en-US' : currentLocale === 'tr' ? 'tr-TR' : 'en-US',
    { month: 'long', year: 'numeric' }
  ).format(currentMonth);

  // Get day names for header
  const dayNames = useMemo(() => {
    const names: string[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(2024, 0, 7 + i); // Start from Sunday
      const dayName = new Intl.DateTimeFormat(
        currentLocale === 'en' ? 'en-US' : currentLocale === 'tr' ? 'tr-TR' : 'en-US',
        { weekday: 'short' }
      ).format(date);
      names.push(dayName);
    }
    return names;
  }, [currentLocale]);

  // Generate calendar days
  const calendarDays: (number | null)[] = [];
  
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Format display value
  const displayValue = selectedDate ? formatDateForDisplay(selectedDate, currentLocale) : '';

  // Check if a day is disabled
  const isDayDisabled = (day: number): boolean => {
    const dayDate = new Date(year, month, day);
    dayDate.setHours(0, 0, 0, 0);
    return !isDateValid(dayDate, minDateObj || undefined, maxDateObj || undefined);
  };

  return (
    <div className={`${styles.datePickerContainer} ${className}`} ref={containerRef} id={id} data-field={dataField}>
      {label && (
        <label className={styles.label}>
          {label}
        </label>
      )}
      <div className={styles.inputWrapper}>
        <input
          ref={inputRef}
          type="text"
          readOnly
          value={displayValue}
          placeholder={placeholder}
          className={`${styles.input} ${error ? styles.inputError : ''} ${disabled ? styles.inputDisabled : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        />
        <button
          type="button"
          className={styles.calendarIcon}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          aria-label="Open calendar"
        >
          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
          {/* @ts-ignore - react-icons compatibility with React 19 */}
          <HiCalendar size={18} />
        </button>
      </div>
      
      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.calendarHeader}>
            <button
              type="button"
              className={styles.monthNavButton}
              onClick={handlePrevMonth}
              aria-label="Previous month"
            >
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiChevronLeft size={20} />
            </button>
            <h3 className={styles.monthTitle}>{monthName}</h3>
            <button
              type="button"
              className={styles.monthNavButton}
              onClick={handleNextMonth}
              aria-label="Next month"
            >
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiChevronRight size={20} />
            </button>
          </div>

          <div className={styles.calendarGrid}>
            {/* Day names header */}
            <div className={styles.dayNamesRow}>
              {dayNames.map((name, index) => (
                <div key={index} className={styles.dayName}>
                  {name}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className={styles.daysGrid}>
              {calendarDays.map((day, index) => {
                if (day === null) {
                  return <div key={index} className={styles.emptyDay} />;
                }

                const dayDate = new Date(year, month, day);
                dayDate.setHours(0, 0, 0, 0);
                
                const isToday = isSameDay(dayDate, today);
                const isSelected = selectedDate && isSameDay(dayDate, selectedDate);
                const isDisabled = isDayDisabled(day);

                return (
                  <button
                    key={index}
                    type="button"
                    className={`
                      ${styles.calendarDay}
                      ${isToday ? styles.today : ''}
                      ${isSelected ? styles.selected : ''}
                      ${isDisabled ? styles.disabled : ''}
                    `}
                    onClick={() => !isDisabled && handleDayClick(day)}
                    disabled={isDisabled}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
